export interface PlatformUpdateNotice {
  latestVersion: string;
  minimumVersion?: string;
  required?: boolean;
  title?: string;
  detail?: string;
  downloadUrl: string;
}

export interface PlatformAnnouncement {
  id: string;
  title: string;
  detail: string;
  display: "always" | "once";
  versions?: string[];
  actionLabel?: string;
  actionUrl?: string;
}

export interface PlatformMenuItem {
  id: string;
  label: string;
  url: string;
}

export interface PlatformBootstrap {
  update?: PlatformUpdateNotice;
  announcements: PlatformAnnouncement[];
  menus: PlatformMenuItem[];
}

const EMPTY_BOOTSTRAP: PlatformBootstrap = {
  announcements: [],
  menus: [],
};

export async function loadPlatformBootstrap(): Promise<PlatformBootstrap> {
  const environment = runtimeEnvironment();
  const useDevelopmentProxy =
    environment.DEV === true &&
    typeof window.location?.origin === "string";
  const endpoint =
    environment.VITE_ORNI_PLATFORM_BOOTSTRAP_URL ??
    (useDevelopmentProxy
      ? "/orni-cloud/v1/platform/compatibility"
      : "https://work.orni.app/v1/platform/compatibility");
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 3_000);
  try {
    const url = new URL(
      endpoint,
      window.location?.origin || "http://127.0.0.1",
    );
    url.searchParams.set("version", currentAppVersion());
    url.searchParams.set("platform", navigator.platform || "desktop");
    url.searchParams.set("locale", currentLocale());
    url.searchParams.set("client", "desktop");
    url.searchParams.set("clientVersion", currentAppVersion());
    url.searchParams.set("protocolVersion", "1");
    const response = await fetch(url, {
      signal: controller.signal,
      cache: "no-store",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) return EMPTY_BOOTSTRAP;
    return normalizePlatformBootstrap(await response.json());
  } catch {
    // Update metadata must never prevent local sessions from opening.
    return EMPTY_BOOTSTRAP;
  } finally {
    window.clearTimeout(timeout);
  }
}

export function currentAppVersion(): string {
  return runtimeEnvironment().VITE_ORNI_APP_VERSION ?? "0.1.0";
}

export function updateIsRequired(update: PlatformUpdateNotice): boolean {
  return Boolean(
    update.required ||
      (update.minimumVersion &&
        comparePlatformVersions(currentAppVersion(), update.minimumVersion) < 0),
  );
}

export function updateIsAvailable(update: PlatformUpdateNotice): boolean {
  return (
    updateIsRequired(update) ||
    comparePlatformVersions(currentAppVersion(), update.latestVersion) < 0
  );
}

export function normalizePlatformBootstrap(value: unknown): PlatformBootstrap {
  if (!value || typeof value !== "object") return EMPTY_BOOTSTRAP;
  const source = value as Record<string, unknown>;
  const compatibilityUpdate = normalizeCompatibilityUpdate(source);
  const announcements = uniqueById(
    Array.isArray(source.announcements)
      ? source.announcements.flatMap(normalizeAnnouncement)
      : [],
  );
  const menus = uniqueById(
    Array.isArray(source.menus) ? source.menus.flatMap(normalizeMenu) : [],
  ).slice(0, 3);
  const update = normalizeUpdate(source.update) ?? compatibilityUpdate;
  return { update, announcements, menus };
}

function normalizeCompatibilityUpdate(
  source: Record<string, unknown>,
): PlatformUpdateNotice | undefined {
  if (!source.policy || typeof source.policy !== "object") return undefined;
  const policy = source.policy as Record<string, unknown>;
  if (!policy.clients || typeof policy.clients !== "object") return undefined;
  const desktop = (policy.clients as Record<string, unknown>).desktop;
  if (!desktop || typeof desktop !== "object") return undefined;
  const release = desktop as Record<string, unknown>;
  const latestVersion = textField(release, "recommendedVersion", "recommended_version");
  const minimumVersion = textField(release, "minimumVersion", "minimum_version");
  const downloadUrl = textField(release, "downloadUrl", "download_url");
  const request = source.request && typeof source.request === "object"
    ? source.request as Record<string, unknown>
    : undefined;
  if (!latestVersion || !downloadUrl || !normalizeExternalUrl(downloadUrl)) return undefined;
  return {
    latestVersion,
    minimumVersion,
    required: request?.status === "upgrade_required",
    detail: typeof request?.reason === "string" ? request.reason : undefined,
    downloadUrl: normalizeExternalUrl(downloadUrl) ?? "",
  };
}

function normalizeUpdate(value: unknown): PlatformUpdateNotice | undefined {
  if (!value || typeof value !== "object") return undefined;
  const source = value as Record<string, unknown>;
  const latestVersion = textField(source, "latestVersion", "latest_version");
  const minimumVersion = textField(source, "minimumVersion", "minimum_version");
  const downloadUrl = textField(source, "downloadUrl", "download_url");
  if (
    !latestVersion ||
    !downloadUrl ||
    !normalizeExternalUrl(downloadUrl)
  )
    return undefined;
  return {
    latestVersion,
    minimumVersion,
    required: source.required === true,
    title: typeof source.title === "string" ? source.title : undefined,
    detail: typeof source.detail === "string" ? source.detail : undefined,
    downloadUrl: normalizeExternalUrl(downloadUrl) ?? "",
  };
}

function normalizeAnnouncement(value: unknown): PlatformAnnouncement[] {
  if (!value || typeof value !== "object") return [];
  const source = value as Record<string, unknown>;
  if (
    !nonEmptyText(source.id) ||
    !nonEmptyText(source.title) ||
    !nonEmptyText(source.detail)
  )
    return [];
  return [
    {
      id: source.id.trim(),
      title: source.title.trim(),
      detail: source.detail.trim(),
      display: source.display === "always" ? "always" : "once",
      versions: Array.isArray(source.versions)
        ? source.versions
            .filter(nonEmptyText)
            .map((version) => version.trim())
        : undefined,
      actionLabel: textField(source, "actionLabel", "action_label"),
      actionUrl: normalizeExternalUrl(
        textField(source, "actionUrl", "action_url") ?? "",
      ),
    },
  ];
}

function normalizeMenu(value: unknown): PlatformMenuItem[] {
  if (!value || typeof value !== "object") return [];
  const source = value as Record<string, unknown>;
  if (
    !nonEmptyText(source.id) ||
    !nonEmptyText(source.label) ||
    !nonEmptyText(source.url)
  )
    return [];
  const url = normalizeExternalUrl(source.url);
  return url
    ? [{ id: source.id.trim(), label: source.label.trim(), url }]
    : [];
}

function normalizeExternalUrl(value: string): string | undefined {
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:"
      ? url.toString()
      : undefined;
  } catch {
    return undefined;
  }
}

function textField(
  source: Record<string, unknown>,
  ...keys: string[]
): string | undefined {
  for (const key of keys) {
    if (typeof source[key] === "string" && source[key])
      return source[key] as string;
  }
  return undefined;
}

export function comparePlatformVersions(left: string, right: string): number {
  const leftVersion = parseVersion(left);
  const rightVersion = parseVersion(right);
  const length = Math.max(leftVersion.core.length, rightVersion.core.length);
  for (let index = 0; index < length; index += 1) {
    const difference =
      (leftVersion.core[index] ?? 0) - (rightVersion.core[index] ?? 0);
    if (difference !== 0) return difference > 0 ? 1 : -1;
  }
  if (!leftVersion.prerelease.length && rightVersion.prerelease.length) return 1;
  if (leftVersion.prerelease.length && !rightVersion.prerelease.length) return -1;
  const prereleaseLength = Math.max(
    leftVersion.prerelease.length,
    rightVersion.prerelease.length,
  );
  for (let index = 0; index < prereleaseLength; index += 1) {
    const leftPart = leftVersion.prerelease[index];
    const rightPart = rightVersion.prerelease[index];
    if (leftPart === undefined) return -1;
    if (rightPart === undefined) return 1;
    if (leftPart === rightPart) continue;
    const leftNumber = numericIdentifier(leftPart);
    const rightNumber = numericIdentifier(rightPart);
    if (leftNumber !== undefined && rightNumber !== undefined)
      return leftNumber > rightNumber ? 1 : -1;
    if (leftNumber !== undefined) return -1;
    if (rightNumber !== undefined) return 1;
    return leftPart > rightPart ? 1 : -1;
  }
  return 0;
}

function currentLocale(): string {
  return (
    document.documentElement.lang ||
    navigator.languages?.[0] ||
    navigator.language ||
    "en"
  );
}

interface PlatformRuntimeEnvironment {
  DEV?: boolean;
  VITE_ORNI_APP_VERSION?: string;
  VITE_ORNI_PLATFORM_BOOTSTRAP_URL?: string;
}

function runtimeEnvironment(): PlatformRuntimeEnvironment {
  return (
    import.meta as ImportMeta & {
      env?: PlatformRuntimeEnvironment;
    }
  ).env ?? {};
}

function uniqueById<T extends { id: string }>(items: T[]): T[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

function nonEmptyText(value: unknown): value is string {
  return typeof value === "string" && Boolean(value.trim());
}

function parseVersion(value: string): {
  core: number[];
  prerelease: string[];
} {
  const normalized = value.trim().replace(/^v(?=\d)/i, "").split("+", 1)[0];
  const [core = "0", prerelease = ""] = normalized.split("-", 2);
  return {
    core: core.split(".").map((part) => {
      const match = /^\d+/.exec(part);
      return match ? Number.parseInt(match[0], 10) : 0;
    }),
    prerelease: prerelease.split(".").filter(Boolean),
  };
}

function numericIdentifier(value: string): number | undefined {
  return /^\d+$/.test(value) ? Number.parseInt(value, 10) : undefined;
}
